import React, { useState, useEffect, useRef, useMemo } from 'react';
import { File, FileCode2, FileText, Search } from 'lucide-react';
import { cn } from '@ideoz/ui';
import { extname } from '../utils/path';

export interface FileSearchProps {
  files: string[];
  onSelect: (path: string) => void;
  onClose: () => void;
}

function getQuickOpenIcon(fileName: string) {
  const e = extname(fileName).toLowerCase();
  const common = 'shrink-0';
  switch (e) {
    case '.tsx':
    case '.ts':
    case '.jsx':
    case '.js':
    case '.mjs':
    case '.cjs':
      return <FileCode2 size={15} className={cn(common, 'text-blue-600 dark:text-blue-400')} />;
    case '.json':
      return <File size={15} className={cn(common, 'text-amber-600 dark:text-amber-400')} />;
    case '.css':
    case '.scss':
      return <FileText size={15} className={cn(common, 'text-purple-600 dark:text-purple-400')} />;
    case '.html':
      return <FileText size={15} className={cn(common, 'text-orange-600 dark:text-orange-400')} />;
    case '.md':
      return <FileText size={15} className={cn(common, 'text-slate-600 dark:text-slate-400')} />;
    default:
      return <File size={15} className={cn(common, 'text-slate-500 dark:text-slate-400')} />;
  }
}

export function FileSearch({ files, onSelect, onClose }: FileSearchProps) {
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const filtered = useMemo(() => {
    if (!query.trim()) return files.slice(0, 80);
    const q = query.trim().toLowerCase();
    return files.filter((f) => f.toLowerCase().includes(q)).slice(0, 80);
  }, [files, query]);

  useEffect(() => {
    setActiveIndex(0);
  }, [filtered]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const file = filtered[activeIndex];
        if (file) onSelect(file);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [filtered, activeIndex, onClose, onSelect]);

  useEffect(() => {
    if (!listRef.current) return;
    const item = listRef.current.children[activeIndex] as HTMLElement | undefined;
    item?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  return (
    <div
      className="absolute inset-0 z-50 flex items-start justify-center pt-16 px-4 bg-slate-900/25 dark:bg-black/50 backdrop-blur-sm"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={cn(
          'w-full max-w-xl rounded-2xl overflow-hidden',
          'border border-slate-200/90 dark:border-slate-600/50',
          'shadow-[0_12px_40px_-8px_rgba(15,23,42,0.18),0_0_0_1px_rgba(255,255,255,0.7)_inset]',
          'dark:shadow-[0_12px_40px_-8px_rgba(0,0,0,0.5)]',
          /* Brighter “glass”: high-opacity base so UI doesn’t read as muddy gray */
          'bg-white/[0.94] dark:bg-slate-900/92 backdrop-blur-xl',
        )}
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Search files by name"
      >
        <div className="px-5 pt-5 pb-4 border-b border-slate-200/80 dark:border-slate-600/50">
          <div
            className={cn(
              'flex items-center gap-3 rounded-xl px-3.5 py-2.5',
              'bg-white dark:bg-slate-950/40',
              'border border-slate-200/90 dark:border-slate-500/35',
              'shadow-[inset_0_1px_0_rgba(255,255,255,0.95)]',
            )}
          >
            <Search size={17} className="text-slate-500 dark:text-slate-400 shrink-0" aria-hidden />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search files by name…"
              className={cn(
                'flex-1 min-w-0 bg-transparent border-0 outline-none shadow-none',
                'text-[15px] leading-snug font-mono text-slate-900 dark:text-slate-100',
                'placeholder:text-slate-400 dark:placeholder:text-slate-500',
                'focus-visible:ring-0',
              )}
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
            />
            <kbd className="hidden sm:inline-flex text-[10px] font-semibold text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-md font-mono border border-slate-200 dark:border-slate-600">
              ESC
            </kbd>
          </div>
        </div>

        <div
          ref={listRef}
          className="max-h-[min(22rem,50vh)] overflow-y-auto overflow-x-hidden px-2 py-2 [scrollbar-gutter:stable] bg-slate-50/90 dark:bg-slate-950/35"
        >
          {filtered.length === 0 ? (
            <div className="px-4 py-10 text-center text-slate-500 dark:text-slate-400 text-sm">No files found</div>
          ) : (
            filtered.map((file, i) => {
              const parts = file.split('/');
              const name = parts[parts.length - 1] ?? file;
              const dir = parts.slice(0, -1).join('/');
              const isActive = i === activeIndex;
              return (
                <button
                  key={file}
                  type="button"
                  className={cn(
                    'w-full text-left rounded-lg px-3 py-2.5 flex items-center gap-3 min-w-0 transition-colors',
                    isActive
                      ? cn(
                          'bg-blue-50 dark:bg-blue-950/50',
                          'ring-2 ring-blue-500/35 dark:ring-blue-400/30',
                          'shadow-sm',
                        )
                      : 'hover:bg-white/90 dark:hover:bg-slate-800/60 border border-transparent',
                  )}
                  onClick={() => onSelect(file)}
                  onMouseEnter={() => setActiveIndex(i)}
                >
                  {getQuickOpenIcon(name)}
                  <span className="truncate text-[14px] font-mono font-semibold tracking-tight shrink min-w-0 text-slate-900 dark:text-slate-100">
                    {name}
                  </span>
                  {dir ? (
                    <span
                      className={cn(
                        'truncate text-[12px] font-mono shrink-0 max-w-[45%] ml-auto pl-2 text-right',
                        'text-slate-600 dark:text-slate-400',
                      )}
                      title={dir}
                    >
                      {dir}
                    </span>
                  ) : null}
                </button>
              );
            })
          )}
        </div>

        {filtered.length > 0 && (
          <div className="px-5 py-3.5 border-t border-slate-200/80 dark:border-slate-600/50 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] text-slate-700 dark:text-slate-300 font-medium select-none bg-white/80 dark:bg-slate-900/50">
            <span className="inline-flex items-center gap-1.5">
              <kbd className="text-[10px] px-1.5 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-600 font-mono font-semibold">
                ↑↓
              </kbd>
              <span>navigate</span>
            </span>
            <span className="inline-flex items-center gap-1.5">
              <kbd className="text-[10px] px-1.5 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-600 font-mono font-semibold">
                ↵
              </kbd>
              <span>open</span>
            </span>
            <span className="inline-flex items-center gap-1.5">
              <kbd className="text-[10px] px-1.5 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-600 font-mono font-semibold">
                ESC
              </kbd>
              <span>close</span>
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
